import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Heart, Trophy, DollarSign } from 'lucide-react';

const authAxios = () => {
  const token = localStorage.getItem('bar_owner_token');
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
};

const nextSaturday = () => {
  const d = new Date();
  const days = (6 - d.getDay() + 7) % 7 || 7;
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function OwnerSinglesNightPanel() {
  const [searchParams] = useSearchParams();
  const [minBid, setMinBid] = useState(100);
  const [current, setCurrent] = useState(null);
  const [myBids, setMyBids] = useState([]);
  const [form, setForm] = useState({
    bid_amount: '',
    max_capacity: 100,
    drink_specials: '',
    description: ''
  });
  const targetWeek = nextSaturday();

  const load = async () => {
    try {
      const [minRes, curRes, myRes] = await Promise.all([
        axios.get(`${API}/singles-night/minimum-bid`),
        axios.get(`${API}/singles-night/current-week`),
        authAxios().get(`/singles-night/my-bids`)
      ]);
      setMinBid(minRes.data.minimum_bid);
      setCurrent(curRes.data);
      setMyBids(myRes.data);
    } catch (err) {
      // silent
    }
  };

  useEffect(() => {
    load();
    const sid = searchParams.get('session_id');
    if (searchParams.get('sn_success') && sid) {
      authAxios().get(`/singles-night/bid-status/${sid}`).then(res => {
        if (res.data.payment_status === 'paid') {
          toast.success('Bid paid & submitted! You&apos;re in the running.');
          load();
        }
      });
    }
    if (searchParams.get('sn_cancel')) {
      toast.info('Bid cancelled — no charge applied.');
    }
    // eslint-disable-next-line
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.bid_amount);
    if (!amt || amt < minBid) {
      toast.error(`Minimum bid is $${minBid}`);
      return;
    }
    try {
      const res = await authAxios().post(`/singles-night/place-paid-bid`, {
        week_date: targetWeek,
        bid_amount: amt,
        max_capacity: parseInt(form.max_capacity, 10) || 100,
        drink_specials: form.drink_specials,
        description: form.description
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit bid');
    }
  };

  const activeBid = myBids.find(b => b.week_date === targetWeek && ['active', 'paid', 'winner'].includes(b.status));

  return (
    <Card data-testid="owner-singles-night" className="glass p-6 border border-pink-500/40">
      <div className="flex items-center gap-2 mb-4">
        <Heart className="w-6 h-6 text-pink-400" fill="currentColor" />
        <h2 className="text-xl font-bold text-white">Singles Night Auction</h2>
      </div>

      <div className="text-sm text-slate-300 mb-4 space-y-1">
        <p>Bid to be the <b className="text-pink-400">Official Singles Night Venue</b> for Saturday <b>{targetWeek}</b>.</p>
        <p>Winner gets: 🏆 Top placement on <code>/singles-night</code>, 📣 Fri/Sat 8pm push to every patron, 🎯 badge on your bar card.</p>
        <p className="text-xs text-slate-500">Sealed-bid auction — competitors can&apos;t see your amount. Minimum: <b className="text-green-400">${minBid}</b>. Payment via Stripe, non-refundable on win.</p>
      </div>

      {current && current.has_winner && current.winning_bar && (
        <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/40 rounded">
          <p className="text-xs text-yellow-400">This week&apos;s winner (already resolved)</p>
          <p className="font-bold text-white flex items-center gap-2">
            <Trophy className="w-4 h-4 text-yellow-400" /> {current.winning_bar.name}
          </p>
        </div>
      )}

      {activeBid && (
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/40 rounded">
          <p className="text-xs text-green-400">Your bid for {targetWeek}</p>
          <p className="font-bold text-white">${activeBid.bid_amount} — status: {activeBid.status}</p>
          {activeBid.status === 'winner' && <p className="text-yellow-400 text-sm mt-1">🎉 You won! Prep the venue.</p>}
        </div>
      )}

      {!activeBid && (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs text-slate-400">Your bid amount (USD)</label>
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-slate-400" />
              <Input
                data-testid="sn-bid-amount"
                type="number"
                min={minBid}
                step="25"
                placeholder={`min $${minBid}`}
                value={form.bid_amount}
                onChange={(e) => setForm({ ...form, bid_amount: e.target.value })}
                className="bg-slate-800 border-slate-700"
                required
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400">Max capacity</label>
            <Input
              data-testid="sn-max-capacity"
              type="number"
              value={form.max_capacity}
              onChange={(e) => setForm({ ...form, max_capacity: e.target.value })}
              className="bg-slate-800 border-slate-700"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400">Drink specials (optional)</label>
            <Input
              data-testid="sn-drink-specials"
              placeholder="$5 well drinks, 2-for-1 shots..."
              value={form.drink_specials}
              onChange={(e) => setForm({ ...form, drink_specials: e.target.value })}
              className="bg-slate-800 border-slate-700"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400">Event pitch (optional)</label>
            <Textarea
              data-testid="sn-description"
              placeholder="Speed-dating, DJ, ice-breaker games..."
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-slate-800 border-slate-700"
            />
          </div>
          <Button
            data-testid="submit-sn-bid"
            type="submit"
            className="w-full bg-gradient-to-r from-pink-500 to-red-500 hover:opacity-90 text-white font-bold"
          >
            Submit Sealed Bid & Pay
          </Button>
        </form>
      )}

      <div className="mt-6">
        <p className="text-xs text-slate-400 uppercase tracking-widest mb-2">Your Recent Bids</p>
        {myBids.length === 0 && <p className="text-xs text-slate-500">No bids yet.</p>}
        <div className="space-y-1">
          {myBids.slice(0, 5).map(b => (
            <div key={b.id} className="flex justify-between text-xs p-2 bg-slate-900/60 rounded">
              <span>{b.week_date}</span>
              <span className="text-green-400">${b.bid_amount}</span>
              <span className={
                b.status === 'winner' ? 'text-yellow-400' :
                b.status === 'lost' ? 'text-red-400' :
                b.status === 'paid' ? 'text-blue-400' : 'text-slate-400'
              }>{b.status}</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
